/*
 * WorkAndBuhin.vue の手入力整備追加、工数・工賃反映、inspect description 保存 payload を切り出した処理。
 */
import { Work } from "@/model/Work";

export interface ManualWorkAddStartActionState {
  shouldAddManualWork: boolean;
  shouldLoadManualKouchin: boolean;
  work: Work | null;
}

export interface ManualWorkAddStartApplyEffectState {
  shouldAddManualWork: boolean;
  shouldLoadManualKouchin: boolean;
  work: Work | null;
}

export interface ManualWorkAddCompleteActionState {
  manualSeibiList: Work[];
  manualWorkNm: string;
  manualKousuu: number | null;
  scrollTargetId: string;
  shouldSetLoading: boolean;
  loading: boolean;
  shouldSelectWork: boolean;
  selectWork: Work;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  selectWorkForceSelect: boolean;
  shouldConfirmNoBuhinAfterDelay: boolean;
  confirmDelay: number;
  loadingAfterDelay: boolean;
}

export interface ManualWorkAddDelayedConfirmActionState {
  shouldSetLoading: boolean;
  loading: boolean;
  shouldConfirmNoBuhin: boolean;
}

export interface ManualWorkAddCompleteCommitApplyEffectState {
  manualSeibiList: Work[];
  manualWorkNm: string;
  manualKousuu: number | null;
  scrollTargetId: string;
  shouldSetLoading: boolean;
  loading: boolean;
  shouldSelectWork: boolean;
  selectWork: Work;
  selectWorkIndex: number;
  selectWorkIsManual: boolean;
  selectWorkForceSelect: boolean;
  shouldConfirmNoBuhinAfterDelay: boolean;
  confirmDelay: number;
  loadingAfterDelay: boolean;
}

export type ManualWorkAddCompleteApplyEffectState =
  ManualWorkAddCompleteActionState;

export type ManualWorkAddDelayedConfirmApplyEffectState =
  ManualWorkAddDelayedConfirmActionState;

/*
 * shouldAddManualWork を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function shouldAddManualWork(manualWorkNm: string | null | undefined): boolean {
  return !!manualWorkNm;
}

/*
 * createManualWork を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function createManualWork(
  manualWorkNm: string,
  manualKousuu: number | null
): Work {
  return {
    seibiNm: manualWorkNm,
    seibiDetailId: null,
    seibiId: null,
    buhinSelected: 0,
    kousuu: manualKousuu || 0,
    manualBuhinList: [],
    buhinList: [],
    editMode: false,
    sknRecommendInspectCategoryId: null,
    kouchinZn: 0
  } as any;
}

/*
 * buildManualWorkAddStartActionState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加時の入力有無判定、作業初期値生成、工賃取得 API 実行条件を固定するため。
 */
export function buildManualWorkAddStartActionState(params: {
  manualWorkNm: string | null | undefined;
  manualKousuu: number | null;
}): ManualWorkAddStartActionState {
  if (!shouldAddManualWork(params.manualWorkNm)) {
    return {
      shouldAddManualWork: false,
      shouldLoadManualKouchin: false,
      work: null
    };
  }
  return {
    shouldAddManualWork: true,
    shouldLoadManualKouchin: true,
    work: createManualWork(params.manualWorkNm as string, params.manualKousuu)
  };
}

/*
 * buildManualWorkAddStartApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加開始時の工賃 API 実行条件と対象 work を固定するため。
 */
export function buildManualWorkAddStartApplyEffectState(
  actionState: ManualWorkAddStartActionState
): ManualWorkAddStartApplyEffectState {
  return {
    shouldAddManualWork: actionState.shouldAddManualWork,
    shouldLoadManualKouchin: actionState.shouldLoadManualKouchin,
    work: actionState.work
  };
}

/*
 * applyManualWorkKouchin を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function applyManualWorkKouchin(work: Work, kouchinZn: any): Work {
  return {
    ...work,
    kouchinZn
  };
}

/*
 * appendManualWork を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function appendManualWork(
  manualSeibiList: Work[] | null | undefined,
  work: Work
): Work[] {
  const list = manualSeibiList || [];
  list.push(work);
  return list;
}

/*
 * getAddedManualWorkIndex を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function getAddedManualWorkIndex(manualSeibiList: Work[]): number {
  return manualSeibiList.length - 1;
}

/*
 * buildManualWorkAddCompleteActionState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加後の入力欄 clear、loading、作業選択、遅延部品なし確定の実行条件を固定するため。
 */
export function buildManualWorkAddCompleteActionState(params: {
  manualSeibiList: Work[] | null | undefined;
  work: Work;
}): ManualWorkAddCompleteActionState {
  const manualSeibiList = appendManualWork(params.manualSeibiList, params.work);
  return {
    manualSeibiList,
    manualWorkNm: "",
    manualKousuu: null,
    scrollTargetId: "list-work",
    shouldSetLoading: true,
    loading: true,
    shouldSelectWork: true,
    selectWork: params.work,
    selectWorkIndex: getAddedManualWorkIndex(manualSeibiList),
    selectWorkIsManual: true,
    selectWorkForceSelect: true,
    shouldConfirmNoBuhinAfterDelay: true,
    confirmDelay: 1000,
    loadingAfterDelay: false
  };
}

/*
 * buildManualWorkAddCompleteApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加完了後の入力欄 clear、scroll、loading、作業選択条件を固定するため。
 */
export function buildManualWorkAddCompleteApplyEffectState(
  actionState: ManualWorkAddCompleteActionState
): ManualWorkAddCompleteApplyEffectState {
  return {
    manualSeibiList: actionState.manualSeibiList,
    manualWorkNm: actionState.manualWorkNm,
    manualKousuu: actionState.manualKousuu,
    scrollTargetId: actionState.scrollTargetId,
    shouldSetLoading: actionState.shouldSetLoading,
    loading: actionState.loading,
    shouldSelectWork: actionState.shouldSelectWork,
    selectWork: actionState.selectWork,
    selectWorkIndex: actionState.selectWorkIndex,
    selectWorkIsManual: actionState.selectWorkIsManual,
    selectWorkForceSelect: actionState.selectWorkForceSelect,
    shouldConfirmNoBuhinAfterDelay: actionState.shouldConfirmNoBuhinAfterDelay,
    confirmDelay: actionState.confirmDelay,
    loadingAfterDelay: actionState.loadingAfterDelay
  };
}

/*
 * buildManualWorkAddCompleteCommitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加完了後に component が即時反映する state、scroll、選択、遅延確定条件を固定するため。
 */
export function buildManualWorkAddCompleteCommitApplyEffectState(
  effectState: ManualWorkAddCompleteApplyEffectState
): ManualWorkAddCompleteCommitApplyEffectState {
  return {
    manualSeibiList: effectState.manualSeibiList,
    manualWorkNm: effectState.manualWorkNm,
    manualKousuu: effectState.manualKousuu,
    scrollTargetId: effectState.scrollTargetId,
    shouldSetLoading: effectState.shouldSetLoading,
    loading: effectState.loading,
    shouldSelectWork: effectState.shouldSelectWork,
    selectWork: effectState.selectWork,
    selectWorkIndex: effectState.selectWorkIndex,
    selectWorkIsManual: effectState.selectWorkIsManual,
    selectWorkForceSelect: effectState.selectWorkForceSelect,
    shouldConfirmNoBuhinAfterDelay: effectState.shouldConfirmNoBuhinAfterDelay,
    confirmDelay: effectState.confirmDelay,
    loadingAfterDelay: effectState.loadingAfterDelay
  };
}

/*
 * buildManualWorkAddDelayedConfirmActionState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加の遅延後に loading を戻して部品なし確定へ進む条件を固定するため。
 */
export function buildManualWorkAddDelayedConfirmActionState(params: {
  shouldConfirmNoBuhinAfterDelay: boolean;
  loadingAfterDelay: boolean;
}): ManualWorkAddDelayedConfirmActionState {
  if (!params.shouldConfirmNoBuhinAfterDelay) {
    return {
      shouldSetLoading: false,
      loading: params.loadingAfterDelay,
      shouldConfirmNoBuhin: false
    };
  }
  return {
    shouldSetLoading: true,
    loading: params.loadingAfterDelay,
    shouldConfirmNoBuhin: true
  };
}

/*
 * buildManualWorkAddDelayedConfirmApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力作業追加の遅延後に loading と部品なし確定へ進む副作用条件を固定するため。
 */
export function buildManualWorkAddDelayedConfirmApplyEffectState(
  actionState: ManualWorkAddDelayedConfirmActionState
): ManualWorkAddDelayedConfirmApplyEffectState {
  return {
    shouldSetLoading: actionState.shouldSetLoading,
    loading: actionState.loading,
    shouldConfirmNoBuhin: actionState.shouldConfirmNoBuhin
  };
}

/*
 * buildManualWorkInspectDescription を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function buildManualWorkInspectDescription(data: any, work: Work): any {
  return {
    id: data.id,
    sknUketsukeInspectId: data.sknUketsukeInspectId,
    sknInspectDescriptionId: data.sknInspectDescriptionId,
    sknInspectDescriptionNm: data.sknInspectDescriptionNm,
    sknUketsukeExtendsId: data.sknUketsukeExtendsId,
    sknInspectCommentId: data.sknInspectCommentId,
    sknInspectType: data.sknInspectType,
    branchId: data.branchId,
    inspectResult: data.inspectResult,
    inspectValue: data.inspectValue,
    inspectValueStr: data.inspectValueStr,
    buhinDanaJisha: data.buhinDanaJisha,
    buhinDanaItaku: data.buhinDanaItaku,
    isGaichu: data.isGaichu,
    isNumber: data.isNumber,
    inspectValueList: data.inspectValueList,
    inspectValueViewType: data.inspectValueViewType,
    workBuhinSelected: data.workBuhinSelected,
    inspectCd: data.inspectCd,
    isHoanKijun: data.isHoanKijun,
    manualSeibiList: [buildManualWorkInspectSeibi(work)],
    sknInspectSubCategoryId: data.sknInspectSubCategoryId,
    displayOrder: data.displayOrder,
    subDisplayOrder: data.subDisplayOrder,
    isDiscount: data.isDiscount,
    updatePgid: data.updatePgid,
    doTenken: data.doTenken,
    dataStateKbn: data.dataStateKbn,
    sknInspectSeibiFeeId: data.sknInspectSeibiFeeId
  };
}

/*
 * buildManualWorkInspectSeibi を WorkAndBuhin.vue から分離し、
 * 手入力作業の追加 を親側置換や単体テストで固定するため。
 */
export function buildManualWorkInspectSeibi(work: Work): any {
  return {
    seibiId: work.seibiId,
    seibiDetailId: work.seibiDetailId,
    seibiNm: work.seibiNm,
    buhinSelected: work.buhinSelected,
    kousuu: work.kousuu,
    buhinList: work.buhinList,
    manualBuhinList: work.manualBuhinList,
    sknRecommendInspectCategoryId: work.sknRecommendInspectCategoryId
  };
}
